// @flow

import React from "react";
import { t, gettext } from "ttag";
import {
    PropertyAssets,
    TextualInformation,
    Collapse,
} from "@2po-c21/components";
import { GeneralInformation } from "@components";
import { Row, Col } from "reactstrap";
import { energyUtils, toYesNoNull, toYesNoMaybeNull } from "@utils";

type Props = {
    property: *,
};

const checkUrbanPlanningInfo = (property) => {
    if (property.listingType === "FOR_RENT") {
        return urbanPlanningInformation(property)
            .map((item) => item.value)
            .some(
                (value) =>
                    value === null ||
                    value === undefined ||
                    value ===
                        t`property-details.urban-planning-information.not-provided`,
            );
    }
};

const energyInformation = (energySpecifications) => {
    return [
        {
            label: t`property-details.energy-information.class`,
            value: energySpecifications.energyLabel
                ? energySpecifications.energyLabel
                : t`property-details.energy-information.not-provided`,
        },
        {
            label: t`property-details.energy-information.rapport-PEB`,
            value: energySpecifications.energyReportReference
                ? energySpecifications.energyReportReference
                : t`property-details.energy-information.not-provided`,
        },
        {
            label: t`property-details.energy-information.total-energy`,
            value: buildEnergyValueLabel(
                energySpecifications.totalEnergyConsumption,
            ),
        },
        {
            label: t`property-details.energy-information.energy-score`,
            value: buildEnergyValueLabel(energySpecifications.energyScore),
        },
        {
            label: t`property-details.energy-information.CO2-emmission`,
            value: buildEnergyValueLabel(energySpecifications.co2Emission),
        },
        {
            label: t`property-details.energy-information.sources`,
            value: energySpecifications.energySources?.length
                ? buildEnergySourceList(energySpecifications.energySources)
                : "",
        },
    ];
};

const interiorInformation = (property) => {
    return [
        {
            label: t`property-details.interior-information.surface-total`,
            value:
                property?.surface?.totalSurfaceArea?.value &&
                property.surface.totalSurfaceArea.value != 0
                    ? asM2(property.surface.totalSurfaceArea)
                    : undefined,
        },
        {
            label: t`property-details.interior-information.surface-habitable`,
            value:
                property.surface.habitableSurfaceArea?.value &&
                property.surface.habitableSurfaceArea.value != 0
                    ? asM2(property.surface.habitableSurfaceArea)
                    : undefined,
        },
        {
            label: t`property-details.interior-information.bathroom-number`,
            value: property.rooms.numberOfBathrooms,
        },
        {
            label: t`property-details.interior-information.bedroom-number`,
            value: property.rooms.numberOfBedrooms,
        },
        {
            label: t`property-details.interior-information.cellar`,
            value: toYesNoNull(property.amenities.cellar),
        },
    ].filter(({ value }) => !!value);
};

const exteriorInformation = (property) => {
    return [
        {
            label: t`property-details.exterior-information.terrace`,
            value: toYesNoNull(property.amenities.terrace),
        },
        {
            label: t`property-details.exterior-information.garden-orientation`,
            value: property.orientationGarden,
        },
        {
            label: t`property-details.exterior-information.pool`,
            value: toYesNoNull(property.amenities.swimmingPool),
        },
    ].filter(({ value }) => !!value);
};

const urbanPlanningInformation = (property) => {
    return [
        {
            label: t`property-details.general-information.urban-planning-information`,
            value: property.destination
                ? gettext(
                      `api.property.characterised-residential-aria.${property.destination}`,
                  )
                : t`property-details.general-information.not-provided`,
        },
        {
            label: t`property-details.urban-planning-information.building-permission`,
            value:
                toYesNoNull(property.hasBuildingPermit) ||
                t`property-details.urban-planning-information.not-provided`,
        },
        {
            label: t`property-details.urban-planning-information.subdivision-authorization`,
            value:
                toYesNoNull(property.hasSubdivisionAuthorization) ||
                t`property-details.urban-planning-information.not-provided`,
        },
        {
            label: t`property-details.urban-planning-information.preemption-right`,
            value:
                toYesNoNull(property.hasPreemptionRight) ||
                t`property-details.urban-planning-information.not-provided`,
        },
        {
            label: t`property-details.urban-planning-information.floodZone`,
            value:
                toYesNoMaybeNull(property.isFloodZone) ||
                t`property-details.urban-planning-information.not-provided`,
        },
        {
            label: t`property-details.urban-planning-information.summon`,
            value: gettext(`common.text.${property.summon.toLowerCase()}`),
        },
    ].filter(({ value }) => !!value);
};

const checkRowValues = (rows) => {
    return rows.map((row) => row.value).some((value) => value !== null);
};

const energySpecificationsConverter = energyUtils.unitsConverter;

const energySourcesDictionary = energyUtils.energySourcesDictionary;

const buildEnergyValueLabel = (energyTotal) =>
    energyTotal
        ? energySpecificationsConverter[energyTotal.unit](energyTotal.value)
        : t`property-details.energy-information.not-provided`;

const buildEnergySourceList = (energySources) =>
    energySources
        .map((energySource) => energySourcesDictionary[energySource.name]())
        .join(" - ");

const asM2 = (surface) => {
    if (!surface) return null;

    return energySpecificationsConverter[surface.unit](surface.value);
};

const PropertyDetailsContent = ({ property }: Props) => {
    if (!property) {
        return null;
    }

    const { energySpecifications = {}, assets = [] } = property;

    const propertyExteriorInfo = exteriorInformation(property);
    const propertyInteriorInfo = interiorInformation(property);

    return (
        <>
            <Collapse
                title={t`property-details.general-information.title`}
                collapsed={true}
            >
                <Row>
                    <Col md="12">
                        <GeneralInformation property={property} />
                    </Col>
                </Row>
            </Collapse>
            <Collapse title={t`property-details.energy-information.title`}>
                <Row>
                    <Col md="12">
                        <TextualInformation
                            rows={energyInformation(energySpecifications)}
                        />
                    </Col>
                </Row>
            </Collapse>
            <Collapse title={t`property-details.interior-information.title`}>
                <Row>
                    {propertyInteriorInfo.length > 0 && (
                        <Col md="12">
                            <TextualInformation rows={propertyInteriorInfo} />
                        </Col>
                    )}
                </Row>
            </Collapse>
            <Collapse title={t`property-details.exterior-information.title`}>
                <Row>
                    {propertyExteriorInfo.length > 0 && (
                        <Col md="12">
                            <TextualInformation rows={propertyExteriorInfo} />
                        </Col>
                    )}
                </Row>
            </Collapse>
            {!checkUrbanPlanningInfo(property) && (
                <Collapse
                    title={t`property-details.urban-planning-information.title`}
                >
                    <Row>
                        {checkRowValues(urbanPlanningInformation(property)) && (
                            <Col md="12">
                                <TextualInformation
                                    rows={urbanPlanningInformation(property)}
                                />
                            </Col>
                        )}
                    </Row>
                </Collapse>
            )}
            {assets.length > 0 && (
                <Collapse title={t`property-details.assets.title`}>
                    <PropertyAssets sectionTitle="" assets={assets} />
                </Collapse>
            )}
        </>
    );
};

export default PropertyDetailsContent;
